/**
 * src/components/ui/Input.jsx — labelled text input, wrapping Field.
 *
 * Thin wrapper: the actual visual/validation behaviour lives in Field.jsx
 * and ui.css's `:user-invalid` rules. This exists so a page writes one
 * component instead of a label + input + hint + error paragraph every time.
 */
import { Field, syncAriaInvalid } from './Field.jsx';

/**
 * @param {{
 *   label: string,
 *   hint?: React.ReactNode,
 *   error?: string|null,
 *   required?: boolean,
 *   id?: string,
 * } & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {JSX.Element}
 */
export function Input({ label, hint, error, required, id, onBlur, onInput, ...inputProps }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      {({ inputId, hintId, errorId }) => (
        <input
          id={inputId}
          required={required}
          aria-describedby={hintId}
          aria-errormessage={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          onBlur={(e) => {
            syncAriaInvalid(e);
            onBlur?.(e);
          }}
          onInput={(e) => {
            syncAriaInvalid(e);
            onInput?.(e);
          }}
          {...inputProps}
        />
      )}
    </Field>
  );
}
